import { at } from 'lodash-es';
import { createSelector } from 'reselect';

import { OBJECT_TYPE_COLLECTIONS } from '@iconik-io/iconik-api/src/types/object_types';
import {
  isPermission,
  type Permission,
  type SearchItemEntity,
} from '@iconik-io/shared-lib/src/redux/entities/normalized/search_item';
import { createEntityDenormalizer } from '@iconik-io/shared-lib/src/redux/entities/normalizers';

import type { AppState } from '../../store/types';

const denormalizeCollection = createEntityDenormalizer('collections');

const getCollection = (state: AppState, collectionId: string) =>
  denormalizeCollection(state.entities, collectionId);

const getAcls = (state: AppState, collectionId: string, isShare: boolean) => {
  if (!collectionId) {
    return undefined;
  }
  if (isShare) {
    return { read: true };
  }
  return state.acls.collections.entityCurrentUserAcls[collectionId];
};

export const selectCollectionAsSearchItemEntity = createSelector(
  getCollection,
  getAcls,
  (collection, acls) => {
    let result: SearchItemEntity | undefined;

    if (collection && acls) {
      const permissions: Permission[] = [];
      for (const [permission, allowed] of Object.entries(acls)) {
        if (allowed && isPermission(permission)) {
          permissions.push(permission);
        }
      }
      result = {
        ...collection,
        permissions,
        object_type: OBJECT_TYPE_COLLECTIONS,
        in_collections: collection.in_collections ?? [],
      };
    }

    return result;
  },
);

export type CollectionTitleObject = {
  id: string;
  title: string;
};

export const selectCollectionTitleObjects = createSelector(
  (_state: AppState, ids: string[]) => ids,
  (state: AppState) => state.entities,
  (collectionIds, entities): CollectionTitleObject[] => {
    const items = {
      ...entities.search_items,
      ...entities.collections,
    };
    return at(items, collectionIds)
      .filter((collection) => collection !== undefined)
      .map(({ id, title }) => ({ id, title }));
  },
);
